import type { CategoryItemTree, MenuItem } from '@/types/book_item'

export const buildTreeWithEmptyRoot = (items: CategoryItemTree[]) => {
  const treeItems = buildTree(items)
  const itemTree: CategoryItemTree = {
    category_id: '',
    image: '',
    parent_id: '',
    top: 0,
    sort_order: 0,
    name: '',
    short_description: '',
    information: 0,
    page_url: '',
    children: treeItems
  }
  return itemTree
}

export function buildTree(items: CategoryItemTree[]) {
  const itemMap: {
    [key: string]: CategoryItemTree
  } = {}

  // Initialize each item, prepare the map for quick access, and ensure a children array is present
  items.forEach((item) => {
    item.children = []
    itemMap[item.category_id] = item
  })

  const rootItems: CategoryItemTree[] = []

  items.forEach((item) => {
    if (item.parent_id === '-1' || !itemMap[item.parent_id]) {
      // Item is a root node
      rootItems.push(item)
    } else {
      // Add the current item to its parent's children array
      if (!Array.isArray(itemMap[item.parent_id].children)) {
        itemMap[item.parent_id].children = []
      }
      itemMap[item.parent_id].children!.push(item)
    }
  })

  return rootItems // This will be your tree's root nodes
}

export function treeHasChild(items: CategoryItemTree[]): boolean {
  for (let i = 0; i < items.length; i++) {
    if ((items[i].children ?? []).length > 0) {
      return true
    }
  }
  return false
}

export function toLink(link: string) {
  document.location = link
}

export function generateBreadcrumbs(categoryId: string, categories: MenuItem[]): MenuItem[] {
  // Create a lookup map of categories
  const categoryLookup = categories.reduce(
    (acc, category) => {
      acc[category.category_id] = category
      return acc
    },
    {} as Record<string, MenuItem>
  )

  // Initialize the breadcrumbs array
  const breadcrumbs: MenuItem[] = []

  // Helper function to recursively add category names to breadcrumbs
  function addCategoryToBreadcrumbs(categoryId: string) {
    const category = categoryLookup[categoryId]
    if (category) {
      // Prepend the category name to maintain the order from root to current category
      breadcrumbs.unshift(category)
      // If the category's parent is not the root, recurse with the parent id
      if (category.parent_id !== '-1') {
        addCategoryToBreadcrumbs(category.parent_id)
      }
    }
  }

  // Start the recursion with the specified category id
  addCategoryToBreadcrumbs(categoryId)

  return breadcrumbs
}

// Function to find MenuItem by category_id
export function findMenuItemByCategoryId(
  categoryId: string,
  menuItems: CategoryItemTree[]
): CategoryItemTree | undefined {
  for (const menuItem of menuItems) {
    if (menuItem.category_id === categoryId) {
      return menuItem
    }
    // If there are children, recursively search through them
    if (menuItem.children) {
      const foundInChildren = findMenuItemByCategoryId(categoryId, menuItem.children)
      if (foundInChildren) {
        return foundInChildren
      }
    }
  }
  return undefined // Return undefined if not found
}
